/**
 * Sharing a reading made for yourself (2026-10-19). The server rules live in
 * the backend's reader/service/public_version.py; the spec is the backend's
 * docs/SHARE_PUBLIC_VERSION_SPEC.md (requirement ids SP-…).
 *
 * The endpoints are kept here, not in api-service.js, so this screen can land
 * beside the gift work without touching the same lines.
 */
import axios from 'axios'
import { static_store } from '@/store.js'

const api = () => static_store.apiUrl

export async function getShareState(session_id, reading_session_id) {
  const r = await axios.get(`${api()}/reader/share/state/`, { params: { session_id, reading_session_id } })
  return r.data
}

export async function setCovenShare(session_id, reading_session_id, on) {
  const r = await axios.post(`${api()}/reader/share/coven/`, { session_id, reading_session_id, on: !!on })
  return r.data
}

/**
 * Its lesson (community-lesson build/API.md §4): written when the owner opens
 * "Its lesson", once; `retry` is "Try writing it once more" after a hold.
 */
export async function previewPublicVersion(session_id, reading_session_id, { retry = false } = {}) {
  const r = await axios.post(`${api()}/reader/share/public-preview/`, { session_id, reading_session_id, ...(retry ? { retry: true } : {}) })
  return r.data
}

/** "Shared by [ Star Gazer | A seeker ]": no rewrite, no model call. Resolves the state. */
export async function setLessonCredit(session_id, reading_session_id, credit) {
  const r = await axios.post(`${api()}/reader/share/credit/`, { session_id, reading_session_id, credit: credit === 'name' ? 'name' : 'seeker' })
  return r.data
}

/**
 * Share sheet rev 13 (lesson-redo API.md §9.3): three wordings. Empty `text`:
 * drawn from the reading; given: their own thought, tidied for strangers.
 * Resolves { ok, from, choices[0..3], care }.
 */
export async function askLessonChoices(session_id, reading_session_id, text = '') {
  const r = await axios.post(`${api()}/reader/share/lesson-ask/`, { session_id, reading_session_id, text: String(text || '') })
  return r.data
}

/**
 * Done (§9.4): place new words on the card. Resolves the share state plus
 * { ok, updated, rewoven }, or { ok: false, reason: 'names_someone', line, suggestion }.
 */
export async function setLessonText(session_id, reading_session_id, text, how = 'typed') {
  const r = await axios.post(`${api()}/reader/share/lesson-text/`, { session_id, reading_session_id, text, how: how === 'choice' ? 'choice' : 'typed' })
  return r.data
}

/** The once-only line after the backfill rewrite (§9.5): keep | dismiss | change | private. Resolves the state. */
export async function markRewriteSeen(session_id, reading_session_id, choice) {
  const r = await axios.post(`${api()}/reader/share/rewrite-seen/`, { session_id, reading_session_id, choice })
  return r.data
}

export async function prepareShareLink(session_id, reading_session_id) {
  const r = await axios.post(`${api()}/reader/share/link/`, { session_id, reading_session_id })
  return r.data
}

// ── words ────────────────────────────────────────────────────────────────

/** "Mara, Jamie, Fer, Ash and Rio" */
export function joinNames(names) {
  const n = (names || []).filter(Boolean)
  if (n.length <= 1) return n.join('')
  if (n.length === 2) return `${n[0]} and ${n[1]}`
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`
}

export const initialOf = (name) => ((name || '?').trim().charAt(0) || '?').toUpperCase()

/** The line under the switch on the "Your reading" side. */
export function mineLine({ covenShared = false } = {}) {
  return covenShared ? 'covenShared' : 'justYou'
}

/** The rating gate on the public side (SP-R3, SP-R4): 'unrated' | 'low' | 'ok'. */
export function publicGate(rating, min = 3) {
  if (rating === null || rating === undefined || rating === '') return 'unrated'
  return Number(rating) >= min ? 'ok' : 'low'
}

/** A date like "Oct 12". */
export function shortDate(value) {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * The server refusing the own share screen because the reading was made for
 * someone (400 'gift', views_share._owner_and_reading): it has the gift's share
 * instead. Told apart from a lesson that failed, which offers a retry.
 */
export function isGiftRefusal(e) {
  const r = e && e.response
  return !!(r && r.status === 400 && r.data && r.data.reason === 'gift')
}
